import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { TbLogin2 } from "react-icons/tb";
import { GoogleLogin } from "@react-oauth/google";
import useElementWidth from "../hooks/useElementWidth";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useToaster } from "../context/ToasterContext";
import Services from "../services/Services";
import "./AuthPages.css";

function resolvePostAuthPath(locationState, fallback = "/") {
  const returnTo = String(locationState?.returnTo || "").trim();
  if (returnTo.startsWith("/")) {
    return returnTo;
  }

  const from = String(locationState?.from || "").trim();
  if (!from.startsWith("/") || from === "/login" || from === "/register") {
    return fallback;
  }

  const fromSearch = typeof locationState?.fromSearch === "string" ? locationState.fromSearch : "";
  const fromHash = typeof locationState?.fromHash === "string" ? locationState.fromHash : "";
  return `${from}${fromSearch}${fromHash}`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { meMeetRoomId } = useParams();
  const { login, isAuthenticated } = useAuth();
  const { getTranslation, language, setLanguage, availableLanguages } = useLanguage();
  const { toastError, toastWarning } = useToaster();

  const stateRoomId = location.state?.roomId;
  const effectiveRoomId = meMeetRoomId || stateRoomId;
  const postAuthPath = useMemo(() => {
    const statePath = resolvePostAuthPath(location.state, "");
    if (statePath) return statePath;
    if (effectiveRoomId) return `/memeet/${effectiveRoomId}`;
    return "/";
  }, [location.state, effectiveRoomId]);
  const registerState = useMemo(() => {
    const nextState = { ...(location.state || {}) };
    if (!nextState.returnTo && effectiveRoomId) {
      nextState.returnTo = `/memeet/${effectiveRoomId}`;
    }
    return Object.keys(nextState).length > 0 ? nextState : undefined;
  }, [location.state, effectiveRoomId]);

  const [socialRef, socialWidth] = useElementWidth(400);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [isTokenLoading, setIsTokenLoading] = useState(true);
  // "Beni hatırla" tercihi localStorage'a YAZILIYORDU ama hiçbir yerde geri
  // OKUNMUYORDU: kullanıcı kutuyu işaretleyip dönse bile kutu yine boş
  // geliyor, yeni giriş sessionStorage'a yazılıyor ve tarayıcı kapanınca
  // oturum kayboluyordu. Tercih artık geri yükleniyor.
  // Varsayılan AÇIK: AuthContext.login imzası da `remember = true` diyor,
  // yani asıl niyet buydu; kutunun false ile başlaması onunla çelişiyordu.
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return localStorage.getItem("rememberMe") !== "false";
    } catch {
      return true;
    }
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(postAuthPath, { replace: true });
    }
  }, [isAuthenticated, navigate, postAuthPath]);

  // Get anonymous token for API calls (only once on page load)
  useEffect(() => {
    setIsTokenLoading(true);
    Services.GetToken()
      .then((res) => {
        if (res.data.status) {
          setToken(res.data.result.token);
          Services.CreatePageView(res.data.result.token, "login").catch(() => {});
        }
      })
      .catch((err) => {
        console.error("GetToken error:", err);
      })
      .finally(() => {
        setIsTokenLoading(false);
      });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toastError(getTranslation("invalidCredentials"));
      return;
    }

    if (!token) {
      toastError(getTranslation("somethingWentWrong"));
      return;
    }

    setIsLoading(true);

    try {
      // Perform login with the API token
      const res = await Services.UserLogin(token, email, password);

      if (res.data.status) {
        const userData = res.data.result;
        // Try different possible token field names
        const userToken = userData.token || userData.accessToken || userData.authToken || userData.userToken;

        if (!userToken) {
          console.error("[Login] No token found in login response:", userData);
          toastError(getTranslation("somethingWentWrong"));
          return;
        }

        login(userData, userToken, rememberMe);
        navigate(postAuthPath, { replace: true });
      } else {
        toastError(getTranslation("invalidCredentials"));
      }
    } catch (err) {
      console.error("Login error:", err);
      toastError(getTranslation("somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  // Google ile giris. Dugme ID token verir; gerisi e-posta girisiyle ayni
  // yoldan yurur (login + postAuthPath). "Beni hatirla" her zaman acik: Google
  // akisinda kutu yok ve kullanici acikca oturumda kalmak istiyor.
  const handleGoogleLogin = async (idToken) => {
    if (!idToken || !token) {
      toastError(getTranslation("somethingWentWrong"));
      return;
    }
    setIsLoading(true);
    try {
      const res = await Services.LoginWithGoogle(token, idToken);
      const userData = res?.data?.result || res?.data;
      const userToken = userData?.token || userData?.accessToken || userData?.authToken || userData?.userToken;
      if (res?.data?.status && userToken) {
        login(userData, userToken, true);
        navigate(postAuthPath, { replace: true });
        return;
      }
      console.error("[GoogleLogin] No token in response:", res?.data);
      toastError(getTranslation("googleLoginFailed"));
    } catch (err) {
      console.error("[GoogleLogin] error:", err, err?.response?.data);
      toastError(getTranslation("googleLoginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const handleGoToRegister = () => {
    if (registerState) {
      navigate("/register", { state: registerState });
      return;
    }
    navigate("/register");
  };

  return (
    <div className="auth-container">
      <div className="auth-left-circle"></div>
      <div className="auth-right-circle"></div>

      <button
        onClick={handleGoToRegister}
        className="auth-redirect-button"
      >
        {getTranslation("signup")} <TbLogin2 size={20} />
      </button>

      <div className="auth-language-selector">
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="auth-lang-select"
        >
          {availableLanguages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      <div className="auth-left-section">
        <div className="auth-left-inner">
          <span className="auth-left-text">
            {getTranslation("manageWorkforce")}{" "}
            <span className="auth-left-highlight">{getTranslation("oneSpace")}</span>
          </span>
          <span className="auth-left-subtitle">
            {getTranslation("signInJourney")}
          </span>
        </div>
      </div>

      <div className="auth-right-section">
        <div className="auth-right-inner">
          <div className="auth-logo">
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M4 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
              <path d="M17 10l4-2v8l-4-2v-4z" />
            </svg>
            <span className="auth-logo-text">MeMeet</span>
          </div>

          <span className="auth-right-subtitle">
            {getTranslation("letsManage")}
          </span>

          <form className="auth-form" onSubmit={handleLogin}>
            <label className="auth-input-label">{getTranslation("email")}*</label>
            <input
              className="auth-input"
              type="email"
              placeholder={getTranslation("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <label className="auth-input-label">{getTranslation("password")}*</label>
            <div className="auth-input-container">
              <input
                className="auth-input"
                type={showPassword ? "text" : "password"}
                placeholder={getTranslation("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/\s/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === " ") {
                    e.preventDefault();
                    toastWarning(getTranslation("passwordCannotContainSpaces"));
                  }
                }}
                autoComplete="current-password"
              />
              <span className="auth-password-toggle" onClick={togglePasswordVisibility}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="auth-remember-me">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
              />
              <label htmlFor="rememberMe">{getTranslation("rememberMe")}</label>
            </div>

            <button type="submit" className="auth-button" disabled={isLoading || isTokenLoading || !token}>
              {isLoading || isTokenLoading ? <div className="auth-spinner"></div> : getTranslation("getStarted")}
            </button>
          </form>

          <div className="auth-divider">
            <span className="auth-divider-line"></span>
            <span className="auth-divider-text">{getTranslation("or")}</span>
            <span className="auth-divider-line"></span>
          </div>

          <div className="auth-social-buttons" ref={socialRef}>
            <GoogleLogin
              onSuccess={(res) => handleGoogleLogin(res?.credential)}
              onError={() => toastError(getTranslation("googleLoginFailed"))}
              theme="filled_black"
              text="continue_with"
              shape="rectangular"
              size="large"
              width={socialWidth}
              locale={language}
            />
          </div>

          <span className="auth-terms-text">
            {getTranslation("termsText")}
          </span>

          <span className="auth-switch-text">
            {getTranslation("dontHaveAccount")}{" "}
            <span onClick={handleGoToRegister} className="auth-link">
              {getTranslation("signup")}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
